import { X } from 'lucide-react';
import type { BulkCatalogResult } from '../../api';

interface BulkResultNoticeProps {
  result: BulkCatalogResult;
  entityLabel: string;
  /** Plural noun; defaults to `${entityLabel}s` */
  entityLabelPlural?: string;
  onDismiss: () => void;
}

const VERB: Record<BulkCatalogResult['action'], string> = {
  delete: 'Permanently deleted',
  deactivate: 'Deactivated',
  activate: 'Reactivated',
};

/**
 * Summarises a bulk action: how many were processed and why any were skipped.
 * "in_use" skips are the important case — they explain why a delete left rows behind.
 */
export function BulkResultNotice({
  result,
  entityLabel,
  entityLabelPlural = `${entityLabel}s`,
  onDismiss,
}: BulkResultNoticeProps) {
  const processed = result.processed.length;
  const inUse = result.skipped.filter(s => s.reason === 'in_use');
  const notFound = result.skipped.filter(s => s.reason === 'not_found');
  const errored = result.skipped.filter(s => s.reason === 'error');
  const noun = (n: number) => (n === 1 ? entityLabel : entityLabelPlural);

  const tone =
    processed === 0 && result.skipped.length > 0
      ? 'bg-amber-50 border-amber-200 text-amber-900'
      : 'bg-green-50 border-green-200 text-green-900';

  return (
    <div
      className={`relative p-3 border rounded-lg text-sm ${tone}`}
      role="status"
      data-testid="catalog-bulk-result"
    >
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-2 right-2 p-1 rounded text-current opacity-60 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <p className="pr-6">
        {VERB[result.action]} {processed} of {result.requested} {noun(result.requested)}.
      </p>
      {inUse.length > 0 && (
        <div className="mt-1.5">
          <p>
            {inUse.length} {noun(inUse.length)} kept because complaints reference{' '}
            {inUse.length === 1 ? 'it' : 'them'}. Deactivate {inUse.length === 1 ? 'it' : 'them'}{' '}
            instead to hide from the form while preserving history:
          </p>
          <ul className="mt-1 ml-4 list-disc space-y-0.5">
            {inUse.slice(0, 10).map(s => (
              <li key={s.id}>
                <span className="font-medium">{s.name ?? s.id}</span>
                <span className="opacity-75">
                  {' '}
                  — {s.complaintCount} {s.complaintCount === 1 ? 'complaint' : 'complaints'}
                </span>
              </li>
            ))}
            {inUse.length > 10 && <li className="opacity-75">…and {inUse.length - 10} more</li>}
          </ul>
        </div>
      )}
      {notFound.length > 0 && (
        <p className="mt-1.5 opacity-80">{notFound.length} no longer existed (already removed).</p>
      )}
      {errored.length > 0 && (
        <p className="mt-1.5 text-red-700">
          {errored.length} failed: {errored.map(s => s.name ?? s.id).join(', ')}
        </p>
      )}
    </div>
  );
}
